import React, { useState, useEffect } from 'react'
import { Box, TextField, Typography, IconButton, Autocomplete, Tooltip } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import InfoIcon from '@mui/icons-material/Info'
import { useNotify } from 'react-admin'
import { 
  getCompanyGatedCommunity, 
  setCompanyGatedCommunity, 
  deleteCompanyGatedCommunity 
} from 'api/company'
import { getSearchGatedCommunity, getGatedCommunityById, GatedCommunitySearchRecord } from 'api/gatedCommunity'
import { Label } from 'common/RoundedPanel'
import SmallButton from 'components/SmallButton'

interface GatedCommunityManagementProps {
  companyId: string
}

const GatedCommunityManagement: React.FC<GatedCommunityManagementProps> = ({ companyId }) => {
  const [currentGatedCommunityId, setCurrentGatedCommunityId] = useState<string | null>(null)
  const [currentGatedCommunityName, setCurrentGatedCommunityName] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [searchOptions, setSearchOptions] = useState<GatedCommunitySearchRecord[]>([])
  const [selectedGC, setSelectedGC] = useState<GatedCommunitySearchRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const notify = useNotify()

  const loadGatedCommunity = async () => {
    try {
      setLoading(true)
      const gcId = await getCompanyGatedCommunity(companyId)
      setCurrentGatedCommunityId(gcId)
      
      if (gcId) {
        // Fetch the gated community directly by ID
        try {
          const gcRecord = await getGatedCommunityById(gcId)
          setCurrentGatedCommunityName(gcRecord.name)
        } catch (fetchErr) {
          console.error('Error fetching gated community:', fetchErr)
          setCurrentGatedCommunityName('Unknown')
        }
      } else {
        setCurrentGatedCommunityName(null)
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to load gated community'
      notify(errorMessage, { type: 'error' })
      console.error('Error loading gated community:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadGatedCommunity()
  }, [companyId])

  const searchGatedCommunities = async (searchTerm: string) => {
    try {
      if (searchTerm.length > 0) {
        const results = await getSearchGatedCommunity(searchTerm)
        setSearchOptions(results)
      } else {
        setSearchOptions([])
      }
    } catch (err) {
      console.error('Error searching gated communities:', err)
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
    setSearchValue(currentGatedCommunityName || '')
    setSelectedGC(currentGatedCommunityId && currentGatedCommunityName ? 
      { id: currentGatedCommunityId, name: currentGatedCommunityName } : null)
  }

  const handleSave = async () => {
    if (!selectedGC) return

    try {
      await setCompanyGatedCommunity(companyId, selectedGC.id)
      setIsEditing(false)
      notify('Gated community assigned successfully', { type: 'success' })
      await loadGatedCommunity()
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to set gated community'
      notify(errorMessage, { type: 'error' })
      console.error('Error setting gated community:', err)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setSearchValue('')
    setSelectedGC(null)
    setSearchOptions([])
  }

  const handleDelete = async () => {
    try {
      await deleteCompanyGatedCommunity(companyId)
      notify('Gated community removed successfully', { type: 'success' })
      await loadGatedCommunity()
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to delete gated community'
      notify(errorMessage, { type: 'error' })
      console.error('Error deleting gated community:', err)
    }
  }

  const handleAddNew = () => {
    setIsEditing(true)
    setSearchValue('')
    setSelectedGC(null)
  }

  if (loading) {
    return <Typography>Loading gated community...</Typography>
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Label>Related Gated Community</Label>
        <Tooltip title="If new users join this company via automatic attachment (e.g., through email domain), they will be automatically attached to this Gated Community">
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box height={8} />

      {!isEditing && currentGatedCommunityId && currentGatedCommunityName && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ 
            mb: 1,
            p: 1,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            backgroundColor: '#f9f9f9'
          }}
        >
          <Typography variant="body2">{currentGatedCommunityName}</Typography>
          <Box>
            <IconButton
              size="small"
              onClick={handleEdit}
              sx={{ color: 'primary.main', mr: 1 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      {!isEditing && !currentGatedCommunityId && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No gated community assigned
          </Typography>
          <SmallButton onClick={handleAddNew}>
            Assign Gated Community
          </SmallButton>
        </Box>
      )}

      {isEditing && (
        <Box>
          <Autocomplete
            size="small"
            options={searchOptions}
            getOptionLabel={(option) => option.name}
            value={selectedGC}
            onChange={(_, newValue) => setSelectedGC(newValue)}
            inputValue={searchValue}
            onInputChange={(_, newInputValue) => {
              setSearchValue(newInputValue)
              searchGatedCommunities(newInputValue)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search gated community..."
                variant="outlined"
              />
            )}
            sx={{ mb: 1 }}
          />
          <Box display="flex" gap={1}>
            <SmallButton
              onClick={handleSave}
              disabled={!selectedGC}
            >
              <SaveIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              Save
            </SmallButton>
            <SmallButton onClick={handleCancel}>
              <CancelIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              Cancel
            </SmallButton>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default GatedCommunityManagement
